import BopisAPI from 'custom-extensions/bopis/lib/BopisAPI';

export default class BopisPickupTimeController {

	constructor (host = {}) {

		(this.host = host)?.addController?.(this);

		this.api = new BopisAPI();
		this.leadTimeHours = window.mivaJS?.bopisLeadTimes || {};
		this.storeDetails = {};

	}

	async init () {
		await this.getStoreDetails();
		this.determinePickupTime();
	}

	/**
	 * This handles most of the functionality to get the Store Details, and display the the time the user can pick it up.
	 */
	async getStoreDetails () {
		let store = this.host.store;
		if (this.host.bopisStoreSelected?.identifier) {
			store = this.host.bopisStoreSelected.identifier;
		} else if (this.host.selectedStore?.identifier) {
			store = this.host.selectedStore.identifier;
		}

		if (!store) {
			return;
		}

		if (store?.toString() === this.storeDetails.identifier?.toString()) {
			return;
		}

		this.storeDetails = await this.api.singleLocationLookup(store);
	}

	// eslint-disable-next-line max-lines-per-function
	determinePickupTime (overrideDate = '') {
		if (!this.storeDetails?.identifier) {
			return;
		}

		let leadTime = Number(this.leadTimeHours[this.storeDetails.identifier] || this.leadTimeHours.global) || 3;
		let originalLeadTime = leadTime;

		let originalDate = new Date();
		if (overrideDate) {
			originalDate = new Date(overrideDate);
		}
		originalDate = this.getRoundedDate(5, originalDate);

		let date = new Date(originalDate.getTime());
		let day = date.getDay() + 1;
		let currentTime = date.getTime();

		// Check if order can be picked up today
		let hours = this.getDaysHours(day);
		let formattedDate = this.formatDateYYYYMMDD(date);
		let specialHours = this.getSpecialHours(formattedDate);
		if (specialHours !== false) {
			hours = specialHours;
		}

		const leadTimeHoursElems = document.getElementsByClassName('js-lead-time-hours');
		for (const leadTimeHoursElem of leadTimeHoursElems) {
			leadTimeHoursElem.innerText = leadTime;
		}

		if (hours.closed !== true) {
			let openingTime = hours?.from1?.split(':').map(Number);
			let closingTime = hours?.to1?.split(':').map(Number);

			let openTime = new Date(date).setHours(openingTime[0], openingTime[1]);
			let closedTime = new Date(date).setHours(Number(closingTime[0]) - leadTime, closingTime[1]);

			if (openTime >= currentTime && closedTime >= openTime + leadTime) {
				// Order placed before opening time, set to opening time + leadTimeHours
				let beforeOpenTime = new Date(date).setHours(Number(openingTime[0]) + leadTime, openingTime[1]);
				this.host.storeLoaded = true;
				this.host.formattedPickupTime = `${this.formatTime(beforeOpenTime)} today`;
				this.host.formattedPickupHours = `in <strong>${originalLeadTime} business hour${originalLeadTime > 1 ? 's' : ''}</strong>`;

				return;
			}

			if (openTime <= currentTime && closedTime >= currentTime + leadTime) {
				// User is in range of current hours of today
				let pickupTime = new Date(date).setHours(new Date(date).getHours() + leadTime, new Date(date).getMinutes());
				this.host.storeLoaded = true;
				this.host.formattedPickupTime = `${this.formatTime(pickupTime)} today`;
				this.host.formattedPickupHours = `in <strong>${originalLeadTime} business hour${originalLeadTime > 1 ? 's' : ''}</strong>`;

				return;
			}

			const currentHour = date.getHours();
			let storeHoursOpen = Number(closingTime[0]) - Number(openingTime[0]);
			if (currentHour <= Number(closingTime[0]) && currentHour >= Number(openingTime[0])) {
				// Current hour is in the current Store Hours
				storeHoursOpen = Number(closingTime[0]) - Number(currentHour);
			} else if (currentHour >= Number(closingTime[0])) {
				// If it's past the closing time, set storeHoursOpen to 0, as there are no business hours.
				storeHoursOpen = 0;
			}
			leadTime -= storeHoursOpen;
			leadTime = leadTime < 0 ? 0 : leadTime;
		}

		let hoursFound = 0;
		while (hoursFound === 0) {
			day += 1;
			if (day === 8) {
				day = 1;
			}
			// Add a new day to the date
			date.setDate(date.getDate() + 1);

			// Can we pick it up today?
			hours = this.getDaysHours(day);
			formattedDate = this.formatDateYYYYMMDD(date);
			specialHours = this.getSpecialHours(formattedDate);
			if (specialHours !== false) {
				hours = specialHours;
			}

			if (hours?.closed === true) {
				continue;
			}

			// Order placed before opening time, set to opening time + leadTimeHours
			let openingTime = hours?.from1?.split(':');
			let closingTime = hours?.to1?.split(':');
			let storeHoursOpen = Number(closingTime[0]) - Number(openingTime[0]);
			const openTimeWithLeadTime = Number(openingTime[0]) + leadTime;

			if (openTimeWithLeadTime >= Number(closingTime[0])) {
				leadTime -= storeHoursOpen;
				leadTime = leadTime < 0 ? 0 : leadTime;
				continue;
			}

			let beforeOpenTime = new Date(date).setHours(openTimeWithLeadTime, openingTime[1]);
			this.host.storeLoaded = true;
			this.host.formattedPickupTime = `${this.formatTime(beforeOpenTime)} on ${this.formatDateDayMonthAndDate(date)}`;
			this.host.formattedPickupHours = this.areDatesOneDayApart(originalDate, date) ? ` <strong>tomorrow at ${this.formatTime(beforeOpenTime)}</strong>` : `at <strong>${this.formatTime(beforeOpenTime)} on ${this.formatDateDayMonthAndDate(date)}</strong>`;

			hoursFound = 1;
		}
	}

	/**
	 * Find the openingHours for a store based on the day
	 */
	getDaysHours (day = 0) {
		if (!this.storeDetails?.location?.openingHours?.length) {
			return false;
		}

		return this.storeDetails?.location?.openingHours?.find((openingHours) => Number(openingHours.dayOfWeek) === day);
	}

	/**
	 * Find the specialHours (if any) for a store based on the date (YYYY-MM-DD)
	 */
	getSpecialHours (date = '') {
		if (!this.storeDetails?.specialOpeningHours?.length) {
			return false;
		}

		return this.storeDetails?.specialOpeningHours?.find((specialOpeningHours) => specialOpeningHours.date === date) ?? false;
	}

	/**
	 * Format Date to (YYYY-MM-DD)
	 */
	formatDateYYYYMMDD (date) {
		return date.toLocaleDateString('fr-CA', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
	}

	/**
	 * Format Time to HH:MM am/pm
	 */
	formatTime (date) {
		let hour = new Date(date).getHours();
		let minutes = new Date(date).getMinutes().toString()
			.padStart(2, '0');
		let ampm = hour <= 11 ? 'AM' : 'PM';
		if (hour > 12) {
			hour -= 12;
		}

		return `${hour}:${minutes} ${ampm}`;
	}

	/**
	 * Format date to {DayOfWeek}, {Month} {Date}
	 */
	formatDateDayMonthAndDate (date) {
		const dateTime = new Date(date);
		const days = [
			'Sunday',
			'Monday',
			'Tuesday',
			'Wednesday',
			'Thursday',
			'Friday',
			'Saturday'
		];

		const months = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December'
		];

		return `${days[dateTime.getDay()]}, ${months[dateTime.getMonth()]} ${dateTime.getDate()}`;
	}

	/**
	 * Round to the nearest {minutes} on a date
	 */
	getRoundedDate (minutes, d = new Date()) {
		let ms = 1000 * 60 * minutes;
		let roundedDate = new Date(Math.ceil(d.getTime() / ms) * ms);

		return roundedDate;
	}

	/**
	 * Determine if 2 dates are 1 day apart
	 */
	areDatesOneDayApart (date1, date2) {
		// Clear the time part of the dates
		const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
		const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

		// Convert both dates to milliseconds
		const msPerDay = 24 * 60 * 60 * 1000;
		const timeDifference = Math.abs(d1.getTime() - d2.getTime());

		// Check if the difference is exactly one day
		return timeDifference === msPerDay;
	}

}
